import {Etl} from '../../../_services/Etl';
import {FlowFetchResponse} from '../../../_services/Flow';
import {InvoiceLine, SearchModalState} from './TorqueInvoiceSearch';
import {numberFormat, removeNumberFormat} from '../../../_helpers/form';
import {CustomerValidation} from './CustomerValidation';
import {SalesOrderValidation} from './SalesOrderValidation';
import {CustomerPoValidation} from './CustomerPoValidation';
import {SalesOrgValidation} from './SalesOrgValidation';
import {BusinessLineValidation} from './BusinessLineValidation';

const debounce = require('debounce-promise');

const salesOrgWorkflowIdMap: any = {
    'USS1': 105,
    'USS3': 105,
    'USS4': 105,
    'USS5': 105,
    'USS6': 373,
    'CAS1': 105,
    'CAS6': 317,
}

const salesOrgMap: any = {
    'USS1': 'USS1 - Industrial Connections &amp; Solutions, LLC',
    'USS3': 'USS3 – ABB Inc (RDC)',
    'USS4': 'USS4 – ABB Inc (Services)',
    'USS5': 'USS5 - ABB, Inc',
    'USS6': 'USS6 - ABB Installation Products Inc.',
    'CAS1': 'CAS1 - ABB Industrial Solutions Canada',
    'CAS6': 'CAS6 - ABB Electrification Canada ULC',
};
const businessLineMap: any = {
    'ELSP': 'SP - Smart Power',
    'ELSB': 'SB - Smart Buildings',
    'ELDS': 'DS - Distribution Solution/Packaging',
    'ELIP': 'IP - Installation Products',
    'ELSE': 'SE - Services'
};

const distributionChannelMap: any = {
    '10': '10 - Distribution',
    '20': '20 - Direct/OEM',
    '30': '30 - ABB2ABB',
    '40': '40 - Internal Torque',
};

export const getWorkflowIdForSalesOrg = (salesOrg: string) => {
    if (salesOrgWorkflowIdMap.hasOwnProperty(salesOrg)) {
        return salesOrgWorkflowIdMap[salesOrg];
    }
    return '';
}

/**
 * Asyncselect handler
 */
export const searchInvoices = (pipelineName: string) => debounce((inputValue: string) => {
    const minimumInputLength: number = 3;
    const resultProperty = 'result';
    const resultValueProperty = 'invoiceNumber';
    const resultTextProperty = 'invoiceNumber';

    if (inputValue.length < minimumInputLength) return;

    return Etl.getInstance().process(pipelineName, {q: inputValue})
        .then((response: FlowFetchResponse) => {
            return response.json[resultProperty].map((item: any) => ({
                value: item[resultValueProperty],
                label: item[resultTextProperty],
                text: item[resultTextProperty],
                ...item
            }));
        });
}, 250);

/**
 * Select the passed invoice line
 * @param line
 * @param state
 * @param setState
 */
export const selectInvoiceLine = (line: InvoiceLine, state: SearchModalState, setState: any) => {
    // determine if line is selected
    const lineIndex = state.selectedLines.indexOf(line.id);

    if (lineIndex < 0) {
        setState((state: SearchModalState) => ({
            ...state,
            selectedLines: [
                ...state.selectedLines,
                line.id
            ]
        }));
    }
};

/**
 * Unselect the passed invoice line
 * @param line
 * @param state
 * @param setState
 */
export const unselectInvoiceLine = (line: InvoiceLine, state: SearchModalState, setState: any) => {
    // determine if line is selected
    const lineIndex = state.selectedLines.indexOf(line.id);

    if (lineIndex >= 0) {
        setState((state: SearchModalState) => ({
            ...state,
            selectedLines: [
                ...state.selectedLines.splice(0, lineIndex),
                ...state.selectedLines.splice(lineIndex + 1)
            ]
        }));
    }
};

/**
 * For the currently selected invoice in state, call ETL to get invoice lines
 * @param modalState
 * @param setModalState
 */
export const getLinesForSelectedInvoice = (pipelineName: string, modalState: any, setModalState: any) => {
    setModalState((state: SearchModalState) => ({
        ...state,
        isSearchingLines: true
    }));

    Etl.getInstance().process(pipelineName, {q: modalState.selectedInvoice.value})
        .then((response: FlowFetchResponse) => {
            if (response.ok) {
                setModalState((state: SearchModalState) => ({
                    ...state,
                    lines: response.json.result.map((l: any) => ({
                        ...l,
                        pricingPer: l.pricingPer * 1, /* pricing per may come back as a string from API */
                        netRevenue: calcNetRevenue(l.quantity, l.netPrice, (l.pricingPer * 1)),
                        custom: false
                    }))
                }));
            } else {
                throw Error(response?.json?.message);
            }
        })
        .catch((error: any) => {
            setModalState((state: SearchModalState) => ({
                ...state,
                errorMessage: error?.message || error || 'Error getting lines for entered Invoice#'
            }));
        })
        .finally(() => {
            setModalState((state: SearchModalState) => ({
                ...state,
                isSearchingLines: false
            }));
        })
};

const validationErrorComponentMap: any = {
    'customer': CustomerValidation,
    'salesOrder': SalesOrderValidation,
    'customerPo': CustomerPoValidation,
    'salesOrg': SalesOrgValidation,
    'businessLine': BusinessLineValidation,
};

/**
 * For a given validation error (customer, salesOrder, customerPo) get the corresponding component
 * @param error
 */
export const getValidationErrorComponent = (error: string): string => {
    return validationErrorComponentMap.hasOwnProperty(error) ?
        validationErrorComponentMap[error]
        :
        null;
}

/**
 * Performs a check against the existing entry state to see if the values (Customer, SO#, PO#, etc....) from the chosen invoice
 * conflict with those on the workflow.
 * In the event that no values are set for those fields, default with values from the Invoice
 * NOTE: this method only updates the searchModalState and NOT the workflow directly, that happens later
 *
 * @param _formState
 * @param _form
 * @param searchModalState
 * @param setSearchModalState
 */
// @ts-ignore - ignore unused variables in prototype
export const validateAndSetDefaults = (formState: any, form: any, searchModalState: SearchModalState, setSearchModalState: any, workflowFieldMap: any) => {
    const validationErrors: string[] = [];
    let formStateUpdates = {};

    setSearchModalState((state: SearchModalState) => ({
        ...state,
        validationErrors,
        workingValidationErrorIndex: 0,
        formStateUpdates,
    }));

    return (validationErrors.length === 0);
};

/**
 * Call the onChange handler passed to component to update formState with selected lines and optionally
 * any additional formStateUpdates
 * @param onChange
 * @param value
 * @param searchModalState
 * @param formStateUpdates
 */
export const handleSaveSelectedLines = (onChange: any, value: any, searchModalState: any, workflowFieldMap: any) => {
    console.log('handleSaveSelectedLines -', searchModalState, workflowFieldMap);

    const salesOrgValue = salesOrgMap.hasOwnProperty(searchModalState?.selectedInvoice?.salesOrg) ?
        salesOrgMap[searchModalState.selectedInvoice.salesOrg]
        :
        searchModalState?.selectedInvoice?.salesOrg;

    const formStateUpdates = {
        ...searchModalState.formStateUpdates,

        [`input_${workflowFieldMap.soldToCustomerNumberFieldId}`]: {
            text: searchModalState?.selectedInvoice?.customerNumber,
            label: searchModalState?.selectedInvoice?.customerNumber,
            value: searchModalState?.selectedInvoice?.customerNumber,
        },
        [`input_${workflowFieldMap.soldToCustomerNameFieldId}`]: searchModalState?.selectedInvoice?.customerName,

        [`input_${workflowFieldMap.salesOrgFieldId}`]: {
            text: salesOrgValue,
            label: salesOrgValue,
            value: salesOrgValue,
        },

        [`input_${workflowFieldMap.sapSalesOrderNumberFieldId}`]: searchModalState?.selectedInvoice?.salesOrderNumber,
        [`input_${workflowFieldMap.customerPoNumberFieldId}`]: searchModalState?.selectedInvoice?.customerPoNumber,
        [`input_${workflowFieldMap.customerPoDateFieldId}`]: searchModalState?.selectedInvoice?.customerPoDate,
        [`input_${workflowFieldMap.referenceInvoiceNumberFieldId}`]: searchModalState?.selectedInvoice?.invoiceNumber,
        [`input_${workflowFieldMap.serviceRenderedDateFieldId}`]: searchModalState?.selectedInvoice?.invoiceDate,
        [`input_${workflowFieldMap.pricingDateFieldId}`]: searchModalState?.selectedInvoice?.invoiceDate,
        [`input_${workflowFieldMap.distributionChannelFieldId}`]: searchModalState?.selectedInvoice?.distributionChannel,
        [`input_${workflowFieldMap.distributionChannelFieldId}`]: {
            text: distributionChannelMap[searchModalState?.selectedInvoice?.distributionChannel],
            label: distributionChannelMap[searchModalState?.selectedInvoice?.distributionChannel],
            value: distributionChannelMap[searchModalState?.selectedInvoice?.distributionChannel],
        },

        [`input_${workflowFieldMap.shipToCustomerNumberFieldId}`]: {
            text: searchModalState?.selectedInvoice?.shipToCustomerNumber,
            label: searchModalState?.selectedInvoice?.shipToCustomerNumber,
            value: searchModalState?.selectedInvoice?.shipToCustomerNumber,
        },
        [`input_${workflowFieldMap.shipToCustomerName1FieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerName1,
        [`input_${workflowFieldMap.shipToCustomerAddressFieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerAddress,
        [`input_${workflowFieldMap.shipToCustomerCityFieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerCity,
        [`input_${workflowFieldMap.shipToCustomerStateFieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerState,
        [`input_${workflowFieldMap.shipToCustomerZipFieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerZip,
        [`input_${workflowFieldMap.shipToCustomerCountryFieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerCountry,
        [`input_${workflowFieldMap.shipToCustomerLanguageFieldId}`]: searchModalState?.selectedInvoice?.shipToCustomerLanguage,
    };

    if (workflowFieldMap.businessLineFieldId) {
        formStateUpdates[`input_${workflowFieldMap.businessLineFieldId}`] = {
            text: businessLineMap[searchModalState?.selectedInvoice?.businessLine],
                label: businessLineMap[searchModalState?.selectedInvoice?.businessLine],
                value: businessLineMap[searchModalState?.selectedInvoice?.businessLine],
        };
    }

    if (workflowFieldMap.plantFieldId) {
         const plant = searchModalState?.lines
             //.filter((line: InvoiceLine) => searchModalState.selectedLines.includes(line.id)) - REMOVED FILTER, search all lines
            .find((line: InvoiceLine) => line?.plant?.trim() !== '')
             .plant || 'MANUAL';

        formStateUpdates[`input_${workflowFieldMap.plantFieldId}`] = {
            text: plant,
            label: plant,
            value: plant,
        };
    }

    if (workflowFieldMap.documentCategoryFieldId) {
        formStateUpdates[`input_${workflowFieldMap.documentCategoryFieldId}`] = searchModalState?.selectedInvoice?.documentCategory;
    }

    // update the main form values
    /*
    [`input_${workflowFieldMap.soldToCustomerNumberFieldId}`]: {
        text: searchModalState?.selectedInvoice?.customerNumber,
            label: searchModalState?.selectedInvoice?.customerNumber,
            value: searchModalState?.selectedInvoice?.customerNumber,
    },
    [`input_${workflowFieldMap.soldToCustomerNameFieldId}`]: searchModalState?.selectedInvoice?.customerName
     */

    // fire the passed onChange handler, adding the selected lines to the current value
    // and updating the main form header values
    onChange([
        /* ...value, */
        ...searchModalState.lines
            .filter((line: InvoiceLine) => searchModalState.selectedLines.includes(line.id))
            .map((line: InvoiceLine) => {
                let optionalFields: any = {};
                if (workflowFieldMap.lineDiscount) {
                    optionalFields[`input_${workflowFieldMap.lineDiscount}`] = '0';
                }
                if (workflowFieldMap.lineDiscountPrice) {
                    optionalFields[`input_${workflowFieldMap.lineDiscountPrice}`] = line.netPrice;
                }
                if (workflowFieldMap.linePricingPer) {
                    optionalFields[`input_${workflowFieldMap.linePricingPer}`] = {text: line.pricingPer, label: line.pricingPer, value: line.pricingPer};
                }

                return {
                    [`input_${workflowFieldMap.lineItemInvoiceNumber}`]: line.invoiceNumber,
                    [`input_${workflowFieldMap.lineItemLineNumber}`]: line.lineNumber,
                    [`input_${workflowFieldMap.lineItemMaterialNumber}`]: {text: line.materialNumber, label: line.materialNumber, value: line.materialNumber},
                    [`input_${workflowFieldMap.lineItemQuantity}`]: line.quantity,
                    [`input_${workflowFieldMap.lineItemNetPrice}`]: line.netPrice,
                    [`input_${workflowFieldMap.lineItemNetRevenue}`]: line.netRevenue,
                    [`input_${workflowFieldMap.lineItemSfdcLineNumber}`]: line.sfdcLineNumber,
                    [`input_${workflowFieldMap.lineItemUnitOfMeasure}`]: {text: line.unitOfMeasure, label: line.unitOfMeasure, value: line.unitOfMeasure},
                    [`input_${workflowFieldMap.lineItemShortText}`]: '',
                    ...optionalFields
                };
            })
    ], formStateUpdates);

    console.log('Setting FormState updates', formStateUpdates);
};

const getPricingPerMultiplier = (line: InvoiceLine) => {
    return [1, 100, 1000].includes(line?.pricingPer) ? line.pricingPer : 1;
}

const calcNetRevenue = (quantity: any, netPrice: any, pricingPer: any) => {
    return numberFormat(removeNumberFormat(quantity || '0') * (removeNumberFormat(netPrice || '0') / getPricingPerMultiplier({pricingPer})), 2);
}

// NOTE - there is a ton of code repetition here, this could be re-written to be a bit more succinct
export const handleLineQuantityChange = (line: InvoiceLine, value: string, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                quantity: value,
                netRevenue: calcNetRevenue(value, line.netPrice, line.pricingPer),
            } : l
        ))
    }));
};
export const handleLineQuantityBlur = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                quantity: numberFormat(removeNumberFormat(value), 0),
                netRevenue: calcNetRevenue(value, line.netPrice, line.pricingPer),
            } : l
        ))
    }));
}

export const handleLineNetPriceChange = (line: InvoiceLine, value: string, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                netPrice: value,
                netRevenue: calcNetRevenue(line.quantity, value, line.pricingPer),
            } : l
        ))
    }));
};
export const handleLineNetPriceBlur = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                netPrice: numberFormat(removeNumberFormat(value), 2),
                netRevenue: calcNetRevenue(line.quantity, value, line.pricingPer),
            } : l
        ))
    }));
}

export const handleLineNetRevenueChange = (line: InvoiceLine, value: string, setSearchModalState: any) => {
    const quantity = line.quantity || '1';

    console.log('handleLineNetRevenueBlur', value, ' quantity ', quantity, getPricingPerMultiplier(line), removeNumberFormat(value), removeNumberFormat(quantity));

    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                netRevenue: value,
                netPrice: numberFormat(getPricingPerMultiplier(line) * (removeNumberFormat(value) / removeNumberFormat(quantity)), 2)
            } : l
        ))
    }));
};
export const handleLineNetRevenueBlur = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    const quantity = line.quantity || '1';

    console.log('handleLineNetRevenueBlur', value, ' quantity ', quantity, getPricingPerMultiplier(line), removeNumberFormat(value), removeNumberFormat(quantity));

    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                netRevenue: numberFormat(removeNumberFormat(value), 2),
                netPrice: numberFormat(getPricingPerMultiplier(line) * (removeNumberFormat(value) / removeNumberFormat(quantity)), 2)
            } : l
        ))
    }));
}
export const handleMaterialChange = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                materialNumber: value,
            } : l
        ))
    }));
}
export const handleLineNumberChange = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                lineNumber: value,
            } : l
        ))
    }));
}
export const handleLineUnitOfMeasureChange = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                unitOfMeasure: value,
                netRevenue: calcNetRevenue(line.quantity, line.netPrice, line.pricingPer),
            } : l
        ))
    }));
}

export const handleLinePricingPerChange = (line: InvoiceLine, value: any, setSearchModalState: any) => {
    const pricingPer = [1, 100, 1000].includes(value) ? value : 1;
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        lines: state.lines.map((l: InvoiceLine) => (
            (l.id === line.id) ? {
                ...line,
                pricingPer: pricingPer,
                netRevenue: calcNetRevenue(line.quantity, line.netPrice, pricingPer),
            } : l
        ))
    }));
}

/**
 * for the current validation resolution modal, return the currently selected choice
 * @param setSearchModalState
 */
export const getSelectedValidationResolution = (searchModalState: SearchModalState) => {
    return searchModalState.validationResolutions.hasOwnProperty(searchModalState.validationErrors[searchModalState.workingValidationErrorIndex]) ?
        searchModalState.validationResolutions[searchModalState.validationErrors[searchModalState.workingValidationErrorIndex]]
        :
        undefined;
}

/**
 * for a given resolution modal (field), return a state setter to capture it's value
 * @param field
 * @param setSearchModalState
 */
export const getSelectedValidationResolutionSetter = (field: string, setSearchModalState: any) => (value: string) => {
    setSearchModalState((state: SearchModalState) => ({
        ...state,
        validationResolutions: {
            ...state.validationResolutions,
            [field]: value
        }
    }));
}

/**
 * Get an object to be used to update customer number and name in parent formState
 * @param searchModalState
 */
export const getCustomerFormStateUpdates = (searchModalState: SearchModalState, workflowFieldMap: any) => {
    return {
        [`input_${workflowFieldMap.soldToCustomerNumberFieldId}`]: {
            text: searchModalState?.selectedInvoice?.customerNumber,
            label: searchModalState?.selectedInvoice?.customerNumber,
            value: searchModalState?.selectedInvoice?.customerNumber,
        },
        [`input_${workflowFieldMap.soldToCustomerNameFieldId}`]: searchModalState?.selectedInvoice?.customerName
    };
}

/**
 * Get an object to be used to update SAP Sales Order#
 * @param searchModalState
 */
export const getSalesOrderFormStateUpdates = (searchModalState: SearchModalState, workflowFieldMap: any) => {
    return {
        [`input_${workflowFieldMap.sapSalesOrderNumberFieldId}`]: searchModalState?.selectedInvoice?.salesOrderNumber
    };
}

/**
 * Get an object to be used to update SAP Sales Org
 * @param searchModalState
 */
export const getSalesOrgFormStateUpdates = (searchModalState: SearchModalState, workflowFieldMap: any) => {
    const salesOrg: string = searchModalState?.selectedInvoice?.salesOrg || '';
    if (salesOrgMap.hasOwnProperty(salesOrg)) {
        return {
            [`input_${workflowFieldMap.salesOrgFieldId}`]: {
                text: salesOrgMap[salesOrg],
                label: salesOrgMap[salesOrg],
                value: salesOrgMap[salesOrg],
            }
        };
    }
    return {};
}

/**
 * Get an object to be used to update Business Line
 * @param searchModalState
 */
export const getBusinessLineFormStateUpdates = (businessLine: string, workflowFieldMap: any) => {
    return {
        [`input_${workflowFieldMap.businessLineFieldId}`]: {
            text: businessLine,
            label: businessLine,
            value: businessLine,
        }
    };
}

/**
 * Get an object to be used to update Customer PO#
 * @param searchModalState
 */
export const getCustomerPoNumberFormStateUpdates = (searchModalState: SearchModalState, workflowFieldMap: any) => {
    return {
        [`input_${workflowFieldMap.customerPoNumberFieldId}`]: searchModalState?.selectedInvoice?.customerPoNumber
    };
};

export const getFormStateUpdatesForResolution = (searchModalState: SearchModalState, resolution: string, workflowFieldMap: any) => {
    if (resolution === 'customer') {
        return getCustomerFormStateUpdates(searchModalState, workflowFieldMap);
    } else if (resolution === 'salesOrder') {
        return getSalesOrderFormStateUpdates(searchModalState, workflowFieldMap);
    } else if (resolution === 'customerPo') {
        return getCustomerPoNumberFormStateUpdates(searchModalState, workflowFieldMap);
    }
    return {};
}

/**
 *
 * @param searchModalState
 */
export const getLargestBusinessLine = (searchModalState: SearchModalState) => {
    const businessLineTotals = searchModalState?.lines.reduce((acc, line) => {
        // @ts-ignore - ignore implicit type warning
        const currentVal = acc.hasOwnProperty(line.businessUnit) ? acc[line.businessUnit] : 0;
        const newVal = currentVal + line.netRevenue;

        return {
            ...acc,
            [line.businessUnit]: newVal
        };
    }, {});

    const largestBusinessLine = Object.keys(businessLineTotals).reduce((acc, businessLine) => {
        // @ts-ignore - ignore implicit type warning
        if (!acc.businessLine || acc.total < businessLineTotals[businessLine]) {
            return {
                businessLine: businessLine,
                name: businessLineMap[businessLine],
                // @ts-ignore - ignore implicit type warning
                total: businessLineTotals[businessLine]
            };
        }
        return acc;
    }, {businessLine: '', total: '', name: ''});

    return largestBusinessLine;
};